import { useEffect, useState } from "react";
import { Layout } from "../components/layout.tsx";
import { useAuth } from "../context/auth.tsx";
import { type Lead, listLeads } from "../api/crm.ts";

const STATUS_COLORS: Record<string, string> = {
  NEW: "text-blue-400 bg-blue-900/30",
  RESEARCHING: "text-purple-400 bg-purple-900/30",
  QUALIFIED: "text-green-400 bg-green-900/30",
  PROPOSAL_READY: "text-yellow-400 bg-yellow-900/30",
  WAITING_APPROVAL: "text-orange-400 bg-orange-900/30",
  EMAIL_SENT: "text-cyan-400 bg-cyan-900/30",
  FOLLOW_UP: "text-teal-400 bg-teal-900/30",
  MEETING_BOOKED: "text-indigo-400 bg-indigo-900/30",
  CLIENT: "text-emerald-400 bg-emerald-900/30",
  REJECTED: "text-red-400 bg-red-900/30",
  ARCHIVED: "text-gray-500 bg-gray-800",
};

const ALL_STATUSES = ["NEW", "RESEARCHING", "QUALIFIED", "PROPOSAL_READY", "WAITING_APPROVAL", "EMAIL_SENT", "FOLLOW_UP", "MEETING_BOOKED", "CLIENT", "REJECTED"];

export function LeadsPage() {
  const { token } = useAuth();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(0);
  const limit = 20;

  useEffect(() => {
    if (token == null) return;
    setLoading(true);
    listLeads(token, { search: search || undefined, status: status || undefined, limit, offset: page * limit })
      .then((res) => { setLeads(res.items); setTotal(res.total); })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : "Failed to load leads"))
      .finally(() => setLoading(false));
  }, [token, search, status, page]);

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white">Leads</h1>
          <span className="text-sm text-gray-500">{total} total</span>
        </div>

        <div className="flex gap-3">
          <input
            type="text"
            placeholder="Search leads…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            className="flex-1 bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
          <select
            value={status}
            onChange={(e) => { setStatus(e.target.value); setPage(0); }}
            className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
          >
            <option value="">All statuses</option>
            {ALL_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        {loading && <p className="text-gray-400">Loading…</p>}
        {error != null && <p className="text-red-400">{error}</p>}
        {!loading && leads.length === 0 && <p className="text-gray-500">No leads found.</p>}

        <div className="space-y-2">
          {leads.map((lead) => (
            <div key={lead.id} className="bg-gray-900 border border-gray-800 rounded-lg p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-white truncate">{lead.title}</p>
                  {lead.description && <p className="text-sm text-gray-400 mt-0.5 truncate">{lead.description}</p>}
                  <div className="flex gap-3 mt-1 text-xs text-gray-500">
                    <span>source: {lead.source}</span>
                    {lead.estimatedValue != null && <span>${lead.estimatedValue.toLocaleString()} {lead.currency}</span>}
                    <span>{new Date(lead.createdAt).toLocaleDateString()}</span>
                  </div>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded font-medium shrink-0 ${STATUS_COLORS[lead.status] ?? "text-gray-400 bg-gray-800"}`}>
                  {lead.status}
                </span>
              </div>
            </div>
          ))}
        </div>

        {total > limit && (
          <div className="flex gap-2">
            <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} className="px-3 py-1 rounded bg-gray-800 text-sm text-gray-300 disabled:opacity-40">Previous</button>
            <span className="text-sm text-gray-500 self-center">{page * limit + 1}–{Math.min((page + 1) * limit, total)} of {total}</span>
            <button onClick={() => setPage((p) => p + 1)} disabled={(page + 1) * limit >= total} className="px-3 py-1 rounded bg-gray-800 text-sm text-gray-300 disabled:opacity-40">Next</button>
          </div>
        )}
      </div>
    </Layout>
  );
}
